import { useState, useEffect } from 'react';
import { addWeeks, addDays, calculateWeeklyCapacity } from '../utils/projectUtils';
import { format } from 'date-fns';

/**
 * Hook pour le calcul en temps réel des dates de phases
 * @param {Object} formData - Données du formulaire de création de projet
 * @param {Array} personnesData - Liste des personnes disponibles
 * @returns {Object} Dates calculées pour chaque phase et le projet global
 */
export const useRealTimePhaseCalculation = (formData, personnesData = []) => {
  const [calculatedDates, setCalculatedDates] = useState({});

  useEffect(() => {
    const calculatePhaseDatesRealTime = () => {
      // Date de début du projet (première phase)
      let currentStartDate = formData.date_debut_analyse_design 
        ? new Date(formData.date_debut_analyse_design) 
        : new Date();

      const phasesOrder = ['analyse_design', 'build', 'test'];
      const newCalculatedDates = {};
      let hasValidPhases = false;

      phasesOrder.forEach(phaseKey => {
        const phase = formData.phases?.[phaseKey];
        const budgetField = `budget_${phaseKey}`;
        const budgetJours = parseFloat(formData[budgetField]) || 0;

        // Initialiser la phase même si pas de données
        newCalculatedDates[phaseKey] = {
          date_debut: new Date(currentStartDate),
          date_fin: new Date(currentStartDate),
          duree_semaines: 0,
          capacite_equipe: 0,
          surcharge: false,
          affectations_valides: 0,
          warnings: []
        };

        // Si pas de budget ou pas d'affectations, passer à la phase suivante
        if (!budgetJours || !phase?.affectations?.length) {
          if (budgetJours > 0 && (!phase?.affectations?.length || phase.affectations.every(a => !a.id_personne))) {
            newCalculatedDates[phaseKey].surcharge = true;
            newCalculatedDates[phaseKey].warnings.push('Aucune personne affectée');
          }
          return;
        }

        // Calculer la capacité totale de l'équipe pour cette phase
        let capaciteTotaleEquipe = 0;
        let affectationsValides = 0;
        const warnings = [];

        phase.affectations.forEach(affectation => {
          if (!affectation.id_personne) return;

          const personne = personnesData.find(p => p.id === parseInt(affectation.id_personne));
          if (!personne) {
            warnings.push(`Personne introuvable (ID: ${affectation.id_personne})`);
            return;
          }

          if (!personne.actif) {
            warnings.push(`${personne.prenom} ${personne.nom} n'est plus actif`);
            return;
          }

          const capacitePersonne = calculateWeeklyCapacity(personne, affectation.pourcentage_temps || 100);
          capaciteTotaleEquipe += capacitePersonne;
          affectationsValides++;
        });

        // Calculer la durée nécessaire
        const semainesNecessaires = capaciteTotaleEquipe > 0 
          ? Math.ceil(budgetJours / capaciteTotaleEquipe)
          : 0;

        // Calculer les dates
        const dateDebut = new Date(currentStartDate);
        const dateFin = semainesNecessaires > 0 
          ? addWeeks(dateDebut, semainesNecessaires)
          : new Date(dateDebut);

        newCalculatedDates[phaseKey] = {
          date_debut: dateDebut,
          date_fin: dateFin,
          duree_semaines: semainesNecessaires,
          capacite_equipe: capaciteTotaleEquipe,
          surcharge: capaciteTotaleEquipe === 0 && budgetJours > 0,
          affectations_valides: affectationsValides,
          warnings: warnings,
          budget_jours: budgetJours
        };

        if (semainesNecessaires > 0) {
          hasValidPhases = true;
          // La phase suivante commence le lundi suivant la fin de celle-ci
          currentStartDate = addDays(dateFin, 1);
        }
      });

      // Calculer les dates globales du projet
      const phasesWithDates = Object.values(newCalculatedDates).filter(p => p.duree_semaines > 0);
      
      if (phasesWithDates.length > 0) {
        const premiereDateDebut = Math.min(...phasesWithDates.map(p => p.date_debut.getTime()));
        const derniereDateFin = Math.max(...phasesWithDates.map(p => p.date_fin.getTime()));
        
        newCalculatedDates.projet = {
          date_debut: new Date(premiereDateDebut),
          date_fin: new Date(derniereDateFin),
          duree_totale_semaines: phasesWithDates.reduce((total, p) => total + p.duree_semaines, 0),
          budget_total: phasesWithDates.reduce((total, p) => total + p.budget_jours, 0),
          capacite_moyenne: phasesWithDates.reduce((total, p) => total + p.capacite_equipe, 0) / phasesWithDates.length,
          phases_actives: phasesWithDates.length,
          has_warnings: Object.values(newCalculatedDates).some(p => p.warnings?.length > 0),
          has_errors: Object.values(newCalculatedDates).some(p => p.surcharge)
        };
      } else {
        // Projet sans phases valides
        newCalculatedDates.projet = {
          date_debut: currentStartDate,
          date_fin: currentStartDate,
          duree_totale_semaines: 0,
          budget_total: 0,
          capacite_moyenne: 0,
          phases_actives: 0,
          has_warnings: true,
          has_errors: true
        };
      }

      setCalculatedDates(newCalculatedDates);
    };

    // Déclencher le calcul
    calculatePhaseDatesRealTime();
  }, [
    // Dépendances pour recalculer
    formData.phases,
    formData.budget_analyse_design,
    formData.budget_build,
    formData.budget_test,
    formData.date_debut_analyse_design,
    personnesData
  ]);

  return calculatedDates;
};

/**
 * Hook simplifié pour obtenir seulement les dates du projet
 */
export const useProjectDates = (calculatedDates) => {
  return {
    dateDebut: calculatedDates.projet?.date_debut,
    dateFin: calculatedDates.projet?.date_fin,
    dureeTotal: calculatedDates.projet?.duree_totale_semaines || 0,
    isValid: calculatedDates.projet?.phases_actives > 0
  };
};
